import DOMPurify, { type Config } from 'dompurify'

export type SanitizeHtmlProfile = 'email' | 'richText' | 'document' | 'strict'

const FORBIDDEN_TAGS = ['script', 'iframe', 'object', 'embed', 'form', 'math']

const COMMON_ATTRS = [
  'class',
  'id',
  'title',
  'dir',
  'lang',
  'role',
  'aria-label',
  'aria-hidden',
]

const STYLE_ATTRS = ['style', 'color', 'bgcolor', 'align', 'valign']

const LINK_ATTRS = ['href', 'target', 'rel', 'name']

const IMG_ATTRS = ['src', 'alt', 'width', 'height', 'loading']

const TABLE_ATTRS = [
  'border',
  'cellpadding',
  'cellspacing',
  'colspan',
  'rowspan',
  'width',
  'height',
]

const RICH_TEXT_TAGS = [
  'p',
  'br',
  'div',
  'span',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'strike',
  'ul',
  'ol',
  'li',
  'blockquote',
  'a',
  'font',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'sub',
  'sup',
  'img',
]

const EMAIL_TAGS = [
  ...RICH_TEXT_TAGS,
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'img',
  'center',
  'hr',
  'pre',
  'code',
]

const DOCUMENT_TAGS = [...EMAIL_TAGS, 'caption', 'col', 'colgroup']

const STRICT_TAGS = [
  'p',
  'br',
  'div',
  'span',
  'b',
  'strong',
  'i',
  'em',
  'u',
  'ul',
  'ol',
  'li',
  'a',
]

const PROFILE_CONFIG: Record<SanitizeHtmlProfile, Config> = {
  email: {
    ALLOWED_TAGS: EMAIL_TAGS,
    ALLOWED_ATTR: [
      ...COMMON_ATTRS,
      ...STYLE_ATTRS,
      ...LINK_ATTRS,
      ...IMG_ATTRS,
      ...TABLE_ATTRS,
    ],
    FORBID_TAGS: FORBIDDEN_TAGS,
    ALLOW_DATA_ATTR: true,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    ADD_ATTR: ['target'],
  },
  richText: {
    ALLOWED_TAGS: RICH_TEXT_TAGS,
    ALLOWED_ATTR: [
      ...COMMON_ATTRS,
      ...STYLE_ATTRS,
      ...LINK_ATTRS,
      ...IMG_ATTRS,
      'size',
      'face',
    ],
    FORBID_TAGS: FORBIDDEN_TAGS,
    ALLOW_DATA_ATTR: true,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  },
  document: {
    ALLOWED_TAGS: DOCUMENT_TAGS,
    ALLOWED_ATTR: [
      ...COMMON_ATTRS,
      ...STYLE_ATTRS,
      ...LINK_ATTRS,
      ...IMG_ATTRS,
      ...TABLE_ATTRS,
    ],
    FORBID_TAGS: FORBIDDEN_TAGS,
    ALLOW_DATA_ATTR: true,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  },
  strict: {
    ALLOWED_TAGS: STRICT_TAGS,
    ALLOWED_ATTR: [...COMMON_ATTRS, ...LINK_ATTRS],
    FORBID_TAGS: FORBIDDEN_TAGS,
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  },
}

/**
 * Sanitize untrusted HTML before assigning to dangerouslySetInnerHTML.
 * Profiles match content sources (email, rich-text notes, docx, AI snippets).
 */
export function sanitizeHtml(
  html: string | null | undefined,
  profile: SanitizeHtmlProfile = 'richText',
): string {
  if (html == null || html === '') {
    return ''
  }

  return DOMPurify.sanitize(html, PROFILE_CONFIG[profile]) as string
}

export const sanitizeEmailHtml = (html: string | null | undefined): string =>
  sanitizeHtml(html, 'email')

export const sanitizeRichTextHtml = (html: string | null | undefined): string =>
  sanitizeHtml(html, 'richText')

export const sanitizeDocumentHtml = (html: string | null | undefined): string =>
  sanitizeHtml(html, 'document')

export const sanitizeStrictHtml = (html: string | null | undefined): string =>
  sanitizeHtml(html, 'strict')
